import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  getFilterSelectedValueService,
  getValue,
  setFilterSelectedValueService,
} from '../../services/get-filters.service';
import {
  FilterSelectedValue,
  SetFilterSelectedValue,
} from './interfaces/filter-selected-value.interface';
import { Filter } from './interfaces/filter.interface';
const FILTERS = 'set-filter-value/REACT_QUERY_KEY';

export interface AppFilterSelectedValue {
  id: string;
  selectedValue: FilterSelectedValue | undefined;
  setSelectedValue: (
    selectedValue: SetFilterSelectedValue,
  ) => Promise<FilterSelectedValue>;
  isFetching: boolean;
}

export const useAppFilterSelectedValue = (
  id: string,
): AppFilterSelectedValue => {
  const queryClient = useQueryClient();
  const {
    // To prevent from unnecessary undefined value, we get the value already in memory.
    data: selectedValue = getValue(id),
    isFetching,
  } = useQuery({
    queryKey: [FILTERS, id],
    queryFn: () => getFilterSelectedValueService(id),
    placeholderData: keepPreviousData,
  });
  const setSelectedValue = useCallback(
    async (selVal: SetFilterSelectedValue) => {
      const setSelVal = { ...selVal, id };
      setFilterSelectedValueService(setSelVal);
      queryClient.refetchQueries({ queryKey: [FILTERS, id] });
      return setSelVal;
    },
    [queryClient, id],
  );

  const value = useMemo(
    (): AppFilterSelectedValue => ({
      id,
      isFetching,
      selectedValue,
      setSelectedValue,
    }),
    [setSelectedValue, selectedValue, isFetching, id],
  );
  return value;
};

export const useAppSetSelectedValue = (id: string) => {
  const queryClient = useQueryClient();
  const value = useMemo(
    () => async (selVal: SetFilterSelectedValue) => {
      const selectedValue = { ...selVal, id };

      setFilterSelectedValueService(selectedValue);
      queryClient.refetchQueries({ queryKey: [FILTERS, id] });
      return selectedValue;
    },
    [id, queryClient],
  );
  return value;
};

/**
 * The returned callback will refresh the selected values available in passed filters.
 * This will trigger updates to all the filters involved.
 * @returns Callback to load selected values.
 */
export const useAppUpdateAllFilterSelectedValue = () => {
  const queryClient = useQueryClient();

  const value = useMemo(
    () => (filters: Filter[]) => {
      filters.forEach((i) => {
        setFilterSelectedValueService(i.selectedValue);
        queryClient.refetchQueries({ queryKey: [FILTERS, i.id] });
      });
    },
    [queryClient],
  );
  return value;
};

/**
 * The returned callback will refresh the filters according to the filter selection passed.
 * This will trigger updates to all the filters involved.
 * @returns Callback to load selected values.
 */
export const useAppUpdateFilterSelectionCallback = () => {
  const queryClient = useQueryClient();

  const value = useMemo(
    () => (filterSelection: FilterSelectedValue[]) => {
      filterSelection.forEach((i) => {
        setFilterSelectedValueService(i);
        queryClient.refetchQueries({ queryKey: [FILTERS, i.id] });
      });
    },
    [queryClient],
  );
  return value;
};
